import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type TouchEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import styles from './Lightbox.module.scss';

export interface LightboxImage {
  url: string;
  altText?: string | null;
}

interface Props {
  images: LightboxImage[];
  startIndex: number;
  onClose: () => void;
}

export function Lightbox({ images, startIndex, onClose }: Props): JSX.Element | null {
  const [index, setIndex] = useState(startIndex);
  const touchStartX = useRef<number | null>(null);
  const total = images.length;
  const hasMany = total > 1;

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + total) % total);
  }, [total]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % total);
  }, [total]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && hasMany) goPrev();
      else if (e.key === 'ArrowRight' && hasMany) goNext();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, goPrev, goNext, hasMany]);

  const onTouchStart = (e: TouchEvent): void => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e: TouchEvent): void => {
    if (touchStartX.current === null) return;
    const endX = e.changedTouches[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 50 || !hasMany) return;
    if (delta > 0) goPrev();
    else goNext();
  };

  if (total === 0) return null;
  const current = images[index];
  if (!current) return null;

  const node = (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Visualização da imagem"
      onClick={onClose}
    >
      <button
        type="button"
        className={`${styles.iconBtn} ${styles.close}`}
        aria-label="Fechar"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <Icon name="close" size={22} />
      </button>

      {hasMany ? (
        <>
          <button
            type="button"
            className={`${styles.iconBtn} ${styles.prev}`}
            aria-label="Imagem anterior"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
          >
            <Icon name="arrow-left" size={22} />
          </button>
          <button
            type="button"
            className={`${styles.iconBtn} ${styles.next}`}
            aria-label="Imagem seguinte"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
          >
            <Icon name="arrow-right" size={22} />
          </button>
        </>
      ) : null}

      <div
        className={styles.stage}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={current.url}
          alt={current.altText ?? ''}
          className={styles.image}
        />
      </div>

      {hasMany ? (
        <div className={styles.counter} aria-live="polite">
          {index + 1} / {total}
        </div>
      ) : null}
    </div>
  );

  return createPortal(node, document.body);
}
